import { convertISODateTimePartsToUTCISO } from 'src/common/date/convertISODateTimePartsToUTCISO';
import type { PassengerRead } from 'src/common/external/bambi-api/bambiApi';

import type { AddTripFormValues, Location, Timing, Trip } from '../FormValues';

export function buildAllowSms(passenger: PassengerRead): boolean | undefined {
  const allowSms = passenger.allow_sms_message ?? false;
  // Facility users aren't able to read this value, so ensure its not updated
  if (typeof allowSms === 'string') {
    return undefined;
  }
  return allowSms;
}

export function buildPassengerPayload(
  passenger: PassengerRead,
  allowSms: boolean | undefined,
  options: { requireId: boolean }
) {
  return {
    ...(options.requireId
      ? { id: passenger.id }
      : passenger.id && { id: passenger.id }),
    first_name: passenger.first_name,
    last_name: passenger.last_name,
    gender: passenger.gender,
    weight:
      passenger.weight && !isNaN(Number(passenger.weight))
        ? Number(passenger.weight)
        : undefined,
    needs_bariatric_transport: passenger.needs_bariatric_transport,
    email: !passenger.email ? '' : passenger.email,
    notes: passenger.notes,
    ...(passenger.dob && { dob: passenger.dob }),
    ...(passenger.phone_number && { phone_number: passenger.phone_number }),
    allow_sms_message: allowSms,
  };
}

export function buildLocationPayload(location: Location) {
  return {
    place_id: location.address.value,
    address_details: location.address_details,
    num_stairs: location.stairs,
    stair_equipment: location.stair_equipment,
    contact_name: location.contact_name,
    contact_phone_number: location.contact_phone_number || null,
    driver_notes: location.driver_notes,
  };
}

export function buildScheduledPickupAt(timing: Timing): string {
  return convertISODateTimePartsToUTCISO({
    date: timing.date,
    time: timing.pickup_time,
  });
}

export function buildAppointmentAt(timing: Timing): string | null {
  return timing.appointment_time
    ? convertISODateTimePartsToUTCISO({
        date: timing.date,
        time: timing.appointment_time,
      })
    : null;
}

export function buildExistingTripPayload(
  trip: Trip,
  formValues: AddTripFormValues
) {
  const allowSms = buildAllowSms(formValues.passenger);
  return {
    booked_by_name: formValues.booked_by_name,
    booked_by_phone_number: formValues.booked_by_phone_number,
    booked_by_phone_extension: formValues.booked_by_phone_extension,
    passenger: buildPassengerPayload(formValues.passenger, allowSms, {
      requireId: true,
    }),
    external_trip_id: trip.external_trip_id,
    payer_id: formValues.pricing.payer,
    payer_passenger_external_id: formValues.pricing.payer_passenger_external_id,
    input_price_cents: trip.input_price_cents || 0,
    is_price_locked: trip.is_price_locked,
    payment_method: formValues.pricing.payment_method,
    pickup: buildLocationPayload(trip.pickupLocation),
    dropoff: buildLocationPayload(trip.dropoffLocation),
    scheduled_pickup_at: buildScheduledPickupAt(trip.timing),
    appointment_at: buildAppointmentAt(trip.timing),
    is_will_call: trip.timing.is_will_call,
    is_wait_and_return_outbound: trip.is_wait_and_return_outbound,
    is_wait_and_return_inbound: trip.is_wait_and_return_inbound,
    space_type: formValues.service_details.space_type,
    service_type: formValues.service_details.service_type,
    dispatcher_notes: trip.dispatcher_notes,
    is_oxygen_required: formValues.service_details.is_oxygen_required,
    oxygen_liters_per_min: formValues.service_details.oxygen_liters_per_min,
    num_attendants_needed: formValues.service_details.num_attendants_needed,
    num_accompanying_passengers:
      formValues.service_details.num_accompanying_passengers,
    load_time_seconds: trip.load_time_seconds ? trip.load_time_seconds : null,
    has_infectious_disease: formValues.service_details.has_infectious_disease,
    unload_time_seconds: trip.unload_time_seconds
      ? trip.unload_time_seconds
      : null,
    tag_ids: trip.trip_tags.map((option) => option.value),
    authorization_id: formValues.pricing.authorization_id,
    insurance_policy_id: formValues.pricing.insurance_policy_id,
    must_provide_wheelchair: formValues.must_provide_wheelchair,
    seat_equipment: formValues.seat_equipment,
    should_avoid_multiload: formValues.service_details.should_avoid_multiload,
  };
}

export function buildNewTripPayload(trip: Trip, formValues: AddTripFormValues) {
  const allowSms = buildAllowSms(formValues.passenger);
  return {
    passenger: buildPassengerPayload(formValues.passenger, allowSms, {
      requireId: false,
    }),
    external_trip_id: trip.external_trip_id,
    payer_passenger_external_id: formValues.pricing.payer_passenger_external_id,
    payer_id: formValues.pricing.payer || '',
    input_price_cents: trip.input_price_cents || 0,
    is_price_locked: trip.is_price_locked,
    payment_method: formValues.pricing.payment_method,
    pickup: buildLocationPayload(trip.pickupLocation),
    dropoff: buildLocationPayload(trip.dropoffLocation),
    scheduled_pickup_at: buildScheduledPickupAt(trip.timing),
    appointment_at: buildAppointmentAt(trip.timing) ?? undefined,
    is_will_call: trip.timing.is_will_call,
    is_wait_and_return_outbound: trip.is_wait_and_return_outbound,
    is_wait_and_return_inbound: trip.is_wait_and_return_inbound,
    space_type: formValues.service_details.space_type,
    service_type: formValues.service_details.service_type,
    dispatcher_notes: trip.dispatcher_notes,
    is_oxygen_required: formValues.service_details.is_oxygen_required,
    oxygen_liters_per_min: formValues.service_details.oxygen_liters_per_min,
    num_attendants_needed: formValues.service_details.num_attendants_needed,
    num_accompanying_passengers:
      formValues.service_details.num_accompanying_passengers,
    has_infectious_disease: formValues.service_details.has_infectious_disease,
    should_avoid_multiload: formValues.service_details.should_avoid_multiload,
    booked_by_name: formValues.booked_by_name,
    booked_by_phone_number: formValues.booked_by_phone_number,
    booked_by_phone_extension: formValues.booked_by_phone_extension,
    load_time_seconds: trip.load_time_seconds ? trip.load_time_seconds : null,
    unload_time_seconds: trip.unload_time_seconds
      ? trip.unload_time_seconds
      : null,
    tag_ids: trip.trip_tags.map((option) => option.value),
    authorization_id: formValues.pricing.authorization_id,
    insurance_policy_id: formValues.pricing.insurance_policy_id,
    must_provide_wheelchair: formValues.must_provide_wheelchair,
    seat_equipment: formValues.seat_equipment,
  };
}
